import React, { useState, useMemo, useEffect, useTransition } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon, FormInput, Loader, SearchInput } from "@src/components/atoms";

function _escapeForRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function _highlightSearchResultInline(text, searchQuery) {
  if (!searchQuery || !text) {
    return [text]; // No query provided, return the original string
  }

  const regex = new RegExp(`(${_escapeForRegExp(searchQuery)})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) => {
    if (index % 2 === 1) {
      // Odd indices represent the matched parts
      return (
        <strong className="bg-orange-100" key={index}>
          {part}
        </strong>
      );
    } else {
      // Even indices represent the unmatched parts
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }
  });
}

const SearchDynamicContent = ({
  allIndustriesData,
  currentPath,
  onSelect,
  className = "",
  resultsClassName = "",
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchPending, startSearchTransition] = useTransition();
  const [alwaysSearch, setAlwaysSearch] = useState(false);

  const searchableData = useMemo(
    () =>
      Object.entries(allIndustriesData)
        .flatMap(([industry, innerObj]) =>
          Object.entries(innerObj).map(([key, value]) => ({
            industry,
            key,
            value,
          })),
        )
        .sort((a, b) => a.key.localeCompare(b.key)),
    [allIndustriesData],
  );

  /** @type {import('@tanstack/react-table').ColumnDef<any, any>[]} */
  const columns = useMemo(
    () => [
      {
        header: "Mode/Industry",
        accessorKey: "industry",
        cell: (info) => <span>{info.getValue()}</span>,
        size: 70,
        maxSize: 70,
      },
      {
        header: "Key",
        accessorKey: "key",
        cell: (info) => (
          <div className="overflow-x-hidden break-all overflow-y-auto">
            {_highlightSearchResultInline(info.getValue(), searchQuery)}
          </div>
        ),
        minSize: 200,
        size: 200,
      },
      {
        header: "Content",
        accessorKey: "value",
        cell: (info) => (
          <div className="max-h-16 max-w-[66vw] break-all whitespace-pre-wrap overflow-y-auto">
            {_highlightSearchResultInline(
              info.getValue()?.[0]?.content_value,
              searchQuery,
            )}
          </div>
        ),
        size: 300,
        maxSize: 500,
      },
    ],
    [searchQuery],
  );

  const shouldSearch = searchQuery.length >= (alwaysSearch ? 1 : 3);
  useEffect(() => {
    if (!shouldSearch) {
      setSearchResults([]);
      return;
    }

    startSearchTransition(() => {
      const lowerSearch = searchQuery.toLowerCase();
      const filtered = searchableData.filter(({ key, value }) => {
        const keyMatches = key.toLowerCase().includes(lowerSearch);
        const valueMatches =
          Array.isArray(value) &&
          value.some(
            (v) =>
              typeof v.content_value === "string" &&
              v.content_value.toLowerCase().includes(lowerSearch),
          );
        return keyMatches || valueMatches;
      });
      setSearchResults(filtered);
    });
  }, [searchableData, searchQuery, shouldSearch]);

  const table = useReactTable({
    data: searchResults,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <section className={`${className}`}>
      <div className="flex p-4">
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      {!shouldSearch && (
        <>
          {searchQuery.length > 0 && (
            <div className="mx-6 my-2 text-sm text-gray-400">
              Keep typing! Search results will appear here.
              <a
                className="mx-2 font-semibold cursor-pointer"
                onClick={() => setAlwaysSearch(true)}
              >
                Search anyway
              </a>
            </div>
          )}
        </>
      )}
      {shouldSearch && (
        <section
          className={`max-h-80 text-sm overflow-y-scroll overflow-x-scroll m-4 p-4 ${resultsClassName ? resultsClassName : ""} bg-primary-100`}
        >
          {isSearchPending && (
            <h6>
              "{searchQuery}" <Loader size="sm" inline />
            </h6>
          )}
          {!isSearchPending && (
            <>
              <h6 className="pb-2">
                "{searchQuery}" has {searchResults.length} results:
              </h6>
              {searchResults.length > 0 && (
                <div className="w-full overflow-scroll">
                  <table className="w-full border-collapse bg-white">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th
                              key={header.id}
                              className="text-left p-2 border-b border-gray-200"
                              // style={{ width: `${header.getSize()}px` }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          className={`cursor-pointer ${
                            row.original.key === currentPath
                              ? "bg-primary-100 font-bold"
                              : "hover:bg-gray-100"
                          }`}
                          onClick={() => onSelect(row.original.key)}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className="p-2 border-b border-gray-100"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </section>
  );
};

export default SearchDynamicContent;
